// SocketContext.tsx - Add connection status tracking
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import io from "socket.io-client";

type Socket = ReturnType<typeof io>;

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
}

const SocketContext = createContext<SocketContextType | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('connecting');

    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem("authData") || "{}");
        const userId = storedUser?.user?._id;

        if (!userId) {
            console.log("❌ No user ID found for socket connection");
            return;
        }

        console.log("🔌 Creating socket connection for user:", userId);
        setConnectionStatus('connecting');

        const newSocket = io(process.env.REACT_APP_SOCKET_URL!, {
            transports: ["websocket"],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            timeout: 20000,
            forceNew: true,
        });

        // Connection events
        newSocket.on("connect", () => {
            console.log("✅ Socket.IO Connected!");
            setIsConnected(true);
            setConnectionStatus('connected');

            // Join user room
            newSocket.emit("join", userId);
        });

        newSocket.on("disconnect", (reason: any) => {
            console.log("❌ Socket.IO Disconnected:", reason);
            setIsConnected(false);
            setConnectionStatus('disconnected');
        });

        newSocket.on("connect_error", (error: any) => {
            console.error("💥 Socket.IO Connection Error:", error);
            setConnectionStatus('error');
            setIsConnected(false);
        });

        newSocket.on("reconnect", (attemptNumber: any) => {
            console.log(`🔄 Socket.IO Reconnected after ${attemptNumber} attempts`);
            setIsConnected(true);
            setConnectionStatus('connected');
        });

        setSocket(newSocket);

        return () => {
            console.log("🔌 Cleaning up socket connection");
            newSocket.disconnect();
            setSocket(null);
            setIsConnected(false);
        };
    }, []);

    return (
        <SocketContext.Provider value={{ socket, isConnected, connectionStatus }}>
            {children}
        </SocketContext.Provider>
    );
};

export const useSocket = () => {
    const ctx = useContext(SocketContext);
    if (!ctx) throw new Error("useSocket must be used inside SocketProvider");
    return ctx;
};